import { Component, computed, effect, inject, input, output } from '@angular/core';
import { SupplierContractPage } from '../../stores/supplier-contracts.store';
import { NgIcon } from '@ng-icons/core';
import { Badge, Button, Skeleton, TooltipDirective } from 'excellence-ui';
import { Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { DatePipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { SupplierContractScoreColorPipe } from '../../pipes/supplier-contract-score-color-pipe';
import { SupplierContractPriceRangePipe } from '../../pipes/supplier-contract-price-range-pipe';
import { GermanNumberPipe } from '../../../../shared/pipes/german-number-pipe';
import { ListSupplierContracts$Params } from '../../../../core/clients/omni-core/functions';
import { ErrorPage } from '../../../../shared/components/error-page/error-page';
import { SupplierContractLocations } from '../supplier-contract-locations/supplier-contract-locations';
import { SupplierContractEnergyInterfaces } from '../supplier-contract-energy-interfaces/supplier-contract-energy-interfaces';
import {
  ForecastKey,
  SupplierContractForecastStore,
} from '../../stores/supplier-contract-forecast.store';
import { FORECAST_LIMIT } from '../../definitions';
import { InViewportDirective } from '../../../../shared/directives/in-viewport';

@Component({
  selector: 'app-supplier-contracts-table',
  imports: [
    NgIcon,
    Badge,
    Button,
    RouterLink,
    TranslocoPipe,
    DatePipe,
    NgTemplateOutlet,
    SupplierContractScoreColorPipe,
    NgClass,
    SupplierContractPriceRangePipe,
    GermanNumberPipe,
    Skeleton,
    ErrorPage,
    SupplierContractLocations,
    SupplierContractEnergyInterfaces,
    TooltipDirective,
    InViewportDirective,
  ],
  templateUrl: './supplier-contracts-table.html',
})
export class SupplierContractsTable {
  protected router = inject(Router);
  private forecastStore = inject(SupplierContractForecastStore);

  supplierContracts = input.required<SupplierContractPage[]>();
  protected areAllPagesLoading = computed(() =>
    this.supplierContracts().every((page) => page.isLoading),
  );

  organizationId = input.required<string>();

  orderBy = input.required<ListSupplierContracts$Params['orderBy']>();
  orderDirection = input.required<ListSupplierContracts$Params['orderDirection']>();
  fullPrice = input.required<boolean>();
  showLoadMorePlaceholders = input.required<boolean>();
  limit = input.required<number>();
  protected placeholders = computed(() => [...Array(this.limit())]);

  changeOrder = output<{
    orderBy: Exclude<ListSupplierContracts$Params['orderBy'], undefined>;
    orderDirection: Exclude<ListSupplierContracts$Params['orderDirection'], undefined>;
  }>();

  currentYear = input.required<number>();

  protected prices = this.forecastStore.prices;
  protected costs = this.forecastStore.cost;

  protected isAvailableForForecast = computed(() => {
    const currentYear = this.currentYear();
    const now = new Date().getFullYear();
    return currentYear >= now && currentYear <= now + FORECAST_LIMIT;
  });

  protected isPastYear = computed(() => this.currentYear() < new Date().getFullYear());

  constructor() {
    effect(() => {
      if (!this.isAvailableForForecast()) {
        return;
      }
      const pages = this.supplierContracts();
      const currentYear = this.currentYear();
      pages.forEach((page) =>
        page.contracts?.forEach((contract) => {
          const key = this.getForecastKey(contract.id, currentYear);
          if (!this.prices()[key]) {
            this.forecastStore.loadPrice(contract.id, currentYear, null);
          }
          if (!this.costs()[key]) {
            this.forecastStore.loadCost(contract.id, currentYear, null);
          }
        }),
      );
    });
  }

  protected onClickHeader(orderBy: ListSupplierContracts$Params['orderBy']) {
    if (!orderBy) {
      return;
    }
    if (this.orderBy() === orderBy) {
      this.changeOrder.emit({
        orderBy,
        orderDirection: this.orderDirection() === 'asc' ? 'desc' : 'asc',
      });
    } else {
      this.changeOrder.emit({
        orderBy,
        orderDirection: 'desc',
      });
    }
  }

  protected getForecastKey(supplierContractId: string, year: number): ForecastKey {
    return `${supplierContractId}-${year}-ALL`;
  }

  protected onClickReload(supplierContractId: string) {
    const currentYear = this.currentYear();
    this.forecastStore.loadPrice(supplierContractId, currentYear, null);
    this.forecastStore.loadCost(supplierContractId, currentYear, null);
  }
}
